import { Component, input, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';

import { PrismSnackbarService } from '@shure/prism-angular-components';
import { macAddressRegEx, serialNumberRegEx } from '@shure/shared/angular/ui/validators/common';

import { ClaimingFlowService } from '../../../services/claiming-flow.service';
import { formatMacAddress } from '../../../utils/parse-claim-url';

/**
 * Route that handles claim URLs by storing the MAC/serial and redirecting. Currently handles
 * V1 and V2 of the claim URL, but can support any URLs that contain :serial and :mac params.
 *
 * Supports optional query parameters:
 * - `final=true`: Skip the QR scanning step and go directly to the Step 2 selection view
 */
@Component({
	selector: 'sh-claim-redirect-route',
	standalone: true,
	template: ''
})
export class ClaimRedirectRouteComponent implements OnInit {
	public readonly serial = input.required<string, string>({
		transform: (value) => value.toUpperCase()
	});
	public readonly mac = input.required<string, string>({
		transform: (value) => value.toUpperCase()
	});

	constructor(
		private readonly claimingFlowService: ClaimingFlowService,
		private readonly router: Router,
		private readonly activatedRoute: ActivatedRoute,
		private readonly snackbarService: PrismSnackbarService,
		private readonly transloco: TranslocoService
	) {}

	public ngOnInit(): void {
		const serialNumber = this.serial();
		const formattedMacAddress = formatMacAddress(this.mac());

		// If serial number or MAC address has invalid formatting, show error and redirect to /devices
		if (!serialNumberRegEx.test(serialNumber) || !macAddressRegEx.test(formattedMacAddress)) {
			this.snackbarService.open({
				message: this.transloco.translate('cloud.device-management.inventory.invalid-claim-mac-serial-message'),
				actionText: this.transloco.translate('common.buttons.close')
			});

			this.router.navigate(['devices'], { replaceUrl: true });
			return;
		}

		this.claimingFlowService.saveDevice({
			serial: this.serial(),
			mac: formatMacAddress(this.mac())
		});

		// Extract query parameters from the claim URL
		// eslint-disable-next-line dot-notation
		const final = this.activatedRoute.snapshot.queryParams['final'] === 'true';

		// Use the appropriate flag based on whether we should skip to the Step 2 selection view
		if (final) {
			this.claimingFlowService.setGoToClaimLinkSelectionStepFlag();
		} else {
			this.claimingFlowService.setGoToQrClaimingStepFlag();
		}

		this.router.navigate(['devices', 'add'], { replaceUrl: true });
	}
}
